import React, { ReactNode } from "react";
import { IntlShape, MessageDescriptor, useIntl } from "react-intl";
import { msgs } from "../../msgs";
import { GeolocationDescriptor, WindowRange } from "../../types";
import { CollapsibleAffectedList } from "./shared";
import { useCountryName, useJoinAnd } from "./locale-hooks";
import { weekdayNames, allWeekdayCodes } from "../weekday-format";

/**
 * Builds one "<b>{label}</b> de <b>{value}</b>" restriction sentence (e.g. "Estancia mínima de 2
 * noches") — BOTH the restriction-type label and the quantity+unit are bolded, per spec. Shared by
 * the update card's unified "Restricciones" section (min/max stay, min/max notice — see
 * RestrictionRow in update-summary.tsx) AND the create card's own "Restricciones" section
 * (PromotionApprovalSummary), so a restriction reads identically whether it came from creating or
 * updating a promotion.
 */
export function restrictionSentence(
  intl: IntlShape,
  labelMsg: MessageDescriptor,
  value: number,
  unit: "nights" | "days"
): ReactNode {
  const label = intl.formatMessage(labelMsg);
  const formattedValue = intl.formatMessage(
    unit === "nights" ? msgs.approvalNightsValue : msgs.approvalDaysValue,
    { count: value }
  );
  return intl.formatMessage(msgs.approvalRestrictionSentence, {
    label,
    value: formattedValue,
    b: (chunks: ReactNode) => <strong className="font-semibold">{chunks}</strong>,
  });
}

/** start → end date range with the design's light-purple arrow. */
function DateRange({ range }: { range: string }) {
  const [start, end] = range.split(" → ");
  return (
    <span className="flex items-center gap-2 text-[#212121] text-sm">
      <span>{start}</span>
      {end && (
        <>
          <em className="fa fa-arrow-right text-[11px] text-[#4D54D1]" aria-hidden />
          <span>{end}</span>
        </>
      )}
    </span>
  );
}

// dd/mm/yyyy, matching the rest of the card's date formatting.
const fmtDate = (iso?: string) => iso?.slice(0, 10).split("-").reverse().join("/");

/**
 * Renders one or more date-window ranges (the backend's `windowRangeSchema`/`bookingWindowRangeSchema`
 * shape: `{ startDate, endDate, weekDays?, startHour?, endHour?, timeZone? }` — the last three only
 * ever populated on BOOKING windows). A promotion can carry several disjoint ranges for the same
 * window kind (e.g. two separate booking periods), so every range is shown.
 *
 * - A range with an hour band (startHour/endHour set) shows the "Afecta de hh:mm a hh:mm durante
 *   los días de la semana" legend (both the hour range and "días de la semana" bolded) + a
 *   collapsible weekday dropdown (every day when weekDays is empty/omitted — an hour band with no
 *   weekday restriction still applies every day), plus a small muted timezone caption.
 * - A range with ONLY a weekday restriction (no hour band) keeps the simpler "Afecta los días de
 *   la semana" legend + dropdown.
 * - A range with neither shows just its dates, as before.
 */
export function WindowRangesView({ windows }: { windows: WindowRange[] }) {
  const intl = useIntl();
  const bold = (chunks: ReactNode) => <strong className="font-semibold">{chunks}</strong>;
  return (
    <div className="flex flex-col gap-2">
      {windows.map((w, i) => {
        const hasHourRange = w.startHour != null && w.endHour != null;
        const weekdayCodes = w.weekDays && w.weekDays.length > 0 ? w.weekDays : allWeekdayCodes();
        const weekdayItems = weekdayNames(weekdayCodes, intl.locale);
        return (
          <div key={`${w.startDate}-${w.endDate}-${i}`} className="flex flex-col gap-1.5">
            <DateRange range={`${fmtDate(w.startDate)} → ${fmtDate(w.endDate)}`} />
            {hasHourRange ? (
              <CollapsibleAffectedList
                intro={intl.formatMessage(msgs.approvalHourRangeLegend, {
                  startHour: w.startHour,
                  endHour: w.endHour,
                  b: bold,
                })}
                items={weekdayItems}
              />
            ) : (
              w.weekDays &&
              w.weekDays.length > 0 && (
                <CollapsibleAffectedList
                  intro={intl.formatMessage(msgs.approvalWeekdaysLegend, { b: bold })}
                  items={weekdayItems}
                />
              )
            )}
            {/* Shown for ANY booking-window range that carries a timezone (not just hour-range
                ones) — a timezone-only change (same dates, no hour band) would otherwise render
                identically to "no change" without this, since dates alone don't reveal it. */}
            {w.timeZone && (
              <span className="text-[#949494] text-[13px] leading-none">
                {intl.formatMessage(msgs.approvalTimezoneCaption, { timeZone: w.timeZone })}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// This input is the model-generated tool call echoed for approval — it can arrive partial or
// slightly off-shape while the tool call's JSON is still streaming in (tool-input-delta), or
// simply malformed. `countries` is only ever safe to iterate/read `.length` on after this guard.
function geolocationCountries(d: GeolocationDescriptor | null | undefined): string[] {
  return Array.isArray(d?.countries) ? d!.countries : [];
}

// Above this many countries, the list collapses behind a "N countries" toggle instead of being
// spelled out inline — a promotion can restrict/exclude dozens of countries, and spelling them
// all out (even wrapped) turns the approval card into a wall of text. Short lists (the common
// case) still render as a single plain sentence, unchanged from before.
const GEOLOCATION_COLLAPSE_THRESHOLD = 5;

// Renders a GeolocationDescriptor — the SAME component used by the create card
// (includeCountries/excludeCountries) and the update card (changes.geolocation before/after),
// so a geolocation restriction always reads identically regardless of which flow produced it: a
// plain, translated sentence for short lists, or a collapsible disclosure (same <details> pattern
// as the per-child inventory breakdown above) once the list is long enough to otherwise break the
// card's layout.
export function GeolocationValue({
  descriptor,
}: {
  descriptor: GeolocationDescriptor | null | undefined;
}): React.JSX.Element {
  const intl = useIntl();
  const countryName = useCountryName();
  const joinAnd = useJoinAnd();

  const countries = geolocationCountries(descriptor);
  if (!descriptor || descriptor.mode === "all" || countries.length === 0) {
    return (
      <span className="text-[#212121] text-sm">
        {intl.formatMessage(msgs.approvalAllCountries)}
      </span>
    );
  }

  const names = countries.map(countryName);
  const isExcept = descriptor.mode === "exclude";

  if (names.length <= GEOLOCATION_COLLAPSE_THRESHOLD) {
    const joined = joinAnd(names);
    return (
      <span className="text-[#212121] text-sm break-words">
        {isExcept
          ? intl.formatMessage(msgs.approvalCountriesExcept, { countries: joined })
          : joined}
      </span>
    );
  }

  return (
    <details className="group w-full rounded-[10px] border border-[#E5E7EB] bg-white overflow-hidden">
      <summary className="flex h-[43px] items-center justify-between gap-2 px-4 cursor-pointer list-none text-[#212121] text-sm marker:content-none [&::-webkit-details-marker]:hidden">
        {isExcept
          ? intl.formatMessage(msgs.approvalCountriesExceptCount, { count: names.length })
          : intl.formatMessage(msgs.approvalCountriesCount, { count: names.length })}
        <em className="fa fa-chevron-down text-[13px] text-[#212121]" aria-hidden />
      </summary>
      <div className="flex flex-col gap-1 max-h-[168px] overflow-y-auto px-4 pb-3">
        {names.map(name => (
          <span key={name} className="text-[#212121] text-sm break-words">
            {name}
          </span>
        ))}
      </div>
    </details>
  );
}

/**
 * Renders a device-type diff's resulting value: a single device shows plain, no legend (matching
 * the design's "only show the count/collapsible when there's something to disclose" rule); two or
 * more show the "Afecta a X dispositivos" bold-count legend + collapsible list, same pattern as
 * ChainAffectedEngines for child engines.
 */
export function DeviceTypesValue({
  devices,
  intl,
}: {
  devices: string[];
  intl: IntlShape;
}): React.JSX.Element {
  if (devices.length <= 1) {
    return <span className="text-[#212121] text-sm break-words">{devices[0] ?? "—"}</span>;
  }
  return (
    <CollapsibleAffectedList
      intro={intl.formatMessage(msgs.approvalDeviceTypesIntro, {
        count: devices.length,
        b: (chunks: ReactNode) => <strong className="font-semibold">{chunks}</strong>,
      })}
      items={devices}
    />
  );
}

/**
 * Renders a geolocation diff's resulting value for the UPDATE approval card's unified
 * "Geolocalización" section — unlike the CREATE card's GeolocationValue (a plain sentence for
 * short lists, a threshold-gated collapsible for long ones), this ALWAYS shows the "Afecta a X
 * países incluidos/excluidos" legend + collapsible list, regardless of count, per the update
 * card's own spec. Only one of include/exclude is ever active at a time (a promotion's
 * geolocation restriction is mutually exclusive), so this renders whichever `descriptor.mode` is.
 */
export function GeolocationUpdateValue({
  descriptor,
  intl,
}: {
  descriptor: GeolocationDescriptor | null | undefined;
  intl: IntlShape;
}): React.JSX.Element {
  const countryName = useCountryName();
  const countries = geolocationCountries(descriptor);
  if (!descriptor || descriptor.mode === "all" || countries.length === 0) {
    return (
      <span className="text-[#212121] text-sm">
        {intl.formatMessage(msgs.approvalAllCountries)}
      </span>
    );
  }
  const names = countries.map(countryName);
  const introMsg =
    descriptor.mode === "exclude"
      ? msgs.approvalGeolocationExcludedIntro
      : msgs.approvalGeolocationIncludedIntro;
  return (
    <CollapsibleAffectedList
      intro={intl.formatMessage(introMsg, {
        count: names.length,
        b: (chunks: ReactNode) => <strong className="font-semibold">{chunks}</strong>,
      })}
      items={names}
    />
  );
}
